import React from "react";
import { Navigate } from "react-router-dom";
import { Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { usePortalRegistrationStatus } from "@/hooks/usePortalRegistrationStatus";

// REQ-PORTAL-0001-4e184 AC-006: Route wrapper that PublicRoute cannot serve.
//
// PublicRoute redirects EVERY authenticated user to /dashboard. That is correct for /login and
// /register only in the "self-registration" world where authenticated ⇒ registered. It breaks the
// world in which a Keycloak identity is pre-provisioned (SSO federation, admin invite, batch import):
// such a user is authenticated but unlinked, and PublicRoute traps them on /dashboard's terminal
// "Account Not Linked" card with no way to reach /register.
//
// AuthenticatedRegistrationRoute is the additive counterpart used ONLY on /register:
//   - status = 'loading'         → spinner
//   - status = 'linked'          → the user is already registered; send them to /dashboard
//   - status = 'unlinked'        → render the wizard so the user can complete registration
//   - status = 'unknown'         → render the wizard (safer than trapping the user on /dashboard;
//                                   the wizard itself is the escape hatch)
//
// PublicRoute stays untouched. This wrapper is added by App.tsx in Phase 3 (CbC); Phase 0 shipped
// the pattern in Pillar2Portal and the four replicas import it here.

const useStyles = makeStyles({
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

export const AuthenticatedRegistrationRoute = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const styles = useStyles();
  const { t } = useTranslation("cbcportal-common", { useSuspense: false });
  const status = usePortalRegistrationStatus();

  if (status === "loading") {
    return (
      <div
        className={styles.loadingContainer}
        role="status"
        aria-label={t("loading", "Loading...")}
        data-testid="registration-route-spinner"
      >
        <Spinner size="extra-large" label={t("loading", "Loading...")} />
      </div>
    );
  }

  if (status === "linked") {
    return <Navigate to="/dashboard" replace />;
  }

  // unlinked or unknown → let the wizard render so the user can either link or self-register.
  return <>{children}</>;
};
